// Drop-in replacement for the Claude-artifact `window.storage` API, backed by
// our own Express server instead. Same method shapes (get/set returning
// { value }), so App.jsx needed zero changes for its storage calls.

const KEY_TO_ROUTE = {
  "fleet-drivers-v4": "drivers",
  "fleet-earnings-v4": "earnings",
  "fleet-global-bonuses-v1": "bonuses",
  "fleet-avatars-v1": "avatars",
  "fleet-payout-decisions-v1": "payout-decisions",
  "fleet-compensation-requests-v1": "compensation-requests",
  "fleet-chat-threads-v1": "chat-threads",
  "fleet-news-v1": "news",
  "fleet-tasks-v1": "tasks",
  "fleet-point-adjustments-v1": "point-adjustments",
  "fleet-cars-v1": "cars",
  "fleet-weekly-revenue-v1": "weekly-revenue",
  "fleet-profit-loss-v1": "profit-loss",
  "fleet-car-repairs-v1": "car-repairs",
  "fleet-company-expenses-v1": "company-expenses",
  "fleet-car-info-v1": "car-info",
  "fleet-company-adjustments-v1": "company-adjustments",
  "fleet-crypto-paper-portfolio-v1": "crypto-paper-portfolio",
  "fleet-crypto-strategy-params-v1": "crypto-strategy-params",
  "fleet-crypto-intraday-portfolio-v1": "crypto-intraday-portfolio",
  "fleet-crypto-intraday-strategy-params-v1": "crypto-intraday-strategy-params",
  "fleet-debt-repayments-v1": "debt-repayments",
  "fleet-driver-debts-v1": "driver-debts",
  "fleet-group-chat-v1": "group-chat-messages",
  "fleet-dm-threads-v1": "dm-threads",
};

// Odczyt z serwera z zabezpieczeniem na słaby zasięg. Wcześniej zwykły
// fetch() bez limitu czasu: jedno zawieszone połączenie (typowe w telefonie
// w samochodzie) blokowało start appki NA ZAWSZE — kierowca widział tylko
// ekran ładowania/czarny ekran. Teraz każda próba ma limit czasu, a nieudany
// odczyt jest ponawiany (2x) na świeżym połączeniu. Jeśli wszystkie próby
// zawiodą, zwracamy null — dokładnie jak wcześniej przy błędzie sieci.
//
// Limity są oparte na BRAKU POSTĘPU, a nie na łącznym czasie: zapytanie jest
// przerywane, gdy serwer nie odpowiada przez X sekund ("firstByte") albo gdy
// w trakcie pobierania przez X sekund nie przyszedł ani jeden bajt ("stall").
// Wcześniej stały limit łącznego czasu (12 s) przerywał też pobieranie, które
// szło, tylko powoli (słaby zasięg, duża rozmowa) — i zaczynał je od zera, przez
// co na wolnym łączu appka potrafiła się nie załadować nigdy.
const ATTEMPTS = [
  { firstByte: 5000, stall: 6000 },
  { firstByte: 8000, stall: 10000 },
  { firstByte: 14000, stall: 14000 },
];
// Cierpliwe limity — dla dużych odczytów (czat) na bardzo słabym łączu; liczy się brak postępu, nie łączny czas.
const PATIENT_ATTEMPTS = [
  { firstByte: 15000, stall: 20000 },
  { firstByte: 20000, stall: 30000 },
  { firstByte: 30000, stall: 45000 },
];
const RETRY_DELAYS_MS = [200, 600];

// Tryb "wolne łącze": gdy kilka odczytów w krótkim czasie zostało przerwanych z braku postępu (telefon na bardzo słabym
// zasięgu), kolejne odczyty dostają cierpliwe limity czasu (przerwanie i start od zera tylko pogarszały sprawę), a appka
// rzadziej odpytuje serwer. Stan pamiętany ok. 15 minut (także po ponownym otwarciu appki).
const SLOW_KEY = "patex-slowlink";
let slowUntil = 0;
try {
  slowUntil = parseInt(localStorage.getItem(SLOW_KEY) || "0", 10) || 0;
} catch (e) {
  slowUntil = 0;
}
let recentAborts = [];
function noteAbort() {
  const now = Date.now();
  recentAborts = recentAborts.filter((t) => now - t < 10 * 60 * 1000);
  recentAborts.push(now);
  if (recentAborts.length >= 3 && now >= slowUntil) {
    slowUntil = now + 15 * 60 * 1000;
    try {
      localStorage.setItem(SLOW_KEY, String(slowUntil));
    } catch (e) {
      /* nic */
    }
    try {
      if (window.__patexMark) window.__patexMark("slowlink");
    } catch (e) {
      /* nic */
    }
  }
}
window.__patexSlowLink = () => Date.now() < slowUntil;
window.__patexNoteAbort = noteAbort; // inne odczyty (czat, koło fortuny) też zgłaszają tu przerwania

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchTextWithStallTimeout(url, limits) {
  const controller = new AbortController();
  let timer = setTimeout(() => controller.abort(), limits.firstByte);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return { res, text: null };
    let text;
    if (res.body && typeof res.body.getReader === "function" && typeof TextDecoder !== "undefined") {
      const reader = res.body.getReader();
      const parts = [];
      let total = 0;
      for (;;) {
        clearTimeout(timer);
        timer = setTimeout(() => controller.abort(), limits.stall); // odnawiane po każdej porcji danych
        const { done, value } = await reader.read();
        if (done) break;
        parts.push(value);
        total += value.length;
      }
      const all = new Uint8Array(total);
      let offset = 0;
      for (const part of parts) {
        all.set(part, offset);
        offset += part.length;
      }
      text = new TextDecoder("utf-8").decode(all);
    } else {
      text = await res.text();
    }
    return { res, text };
  } finally {
    clearTimeout(timer);
  }
}

async function fetchRouteData(route, attempts) {
  const list = attempts || (Date.now() < slowUntil ? PATIENT_ATTEMPTS : ATTEMPTS);
  for (let attempt = 0; attempt < list.length; attempt++) {
    try {
      const { res, text } = await fetchTextWithStallTimeout(`/api/${route}`, list[attempt]);
      if (res.ok) {
        return { ok: true, data: JSON.parse(text), text, version: res.headers.get("X-Collection-Version") || null };
      }
      // Błąd "po naszej stronie" (np. 404) nie zniknie po ponowieniu — nie męcz serwera.
      if (res.status < 500 && res.status !== 408 && res.status !== 429) return { ok: false };
    } catch (e) {
      /* brak postępu (przerwane), błąd sieci albo uszkodzona odpowiedź — spróbuj ponownie */
      if (e && e.name === "AbortError") noteAbort();
    }
    if (attempt < list.length - 1) await sleep(RETRY_DELAYS_MS[attempt]);
  }
  return { ok: false };
}

// ---- Pamięć ostatnich danych w telefonie (Cache Storage) ----
// Ostatnio pobrane dane kierowcy zapisujemy w telefonie. Przy KOLEJNYM uruchomieniu appka
// pokazuje się natychmiast z tych danych (bez czekania na sieć), a świeże dane dociągają się
// zaraz potem w tle. Pierwsze uruchomienie (brak pamięci) działa jak dotąd.
// Odczyty "ścisłe" (podstawa do zapisu/zmiany) NIGDY nie korzystają z tej pamięci.
const DATA_CACHE = "patex-data-v1";
const DATA_CACHE_MAX_AGE_MS = 14 * 24 * 3600 * 1000;
const PERSIST_MIN_GAP_MS = 15000;
const lastPersistAt = {};
async function readCachedRoute(route) {
  try {
    if (typeof caches === "undefined") return null;
    const c = await caches.open(DATA_CACHE);
    const r = await c.match("/__data/" + route);
    if (!r) return null;
    const savedAt = Number(r.headers.get("X-Saved-At") || 0);
    if (!savedAt || Date.now() - savedAt > DATA_CACHE_MAX_AGE_MS) return null;
    const text = await r.text();
    JSON.parse(text); // uszkodzona kopia = brak kopii
    return { text, version: r.headers.get("X-Version") || null };
  } catch (e) {
    return null;
  }
}
function persistRoute(route, text, version, force) {
  try {
    if (typeof caches === "undefined" || !text) return;
    const now = Date.now();
    if (!force && now - (lastPersistAt[route] || 0) < PERSIST_MIN_GAP_MS) return;
    lastPersistAt[route] = now;
    caches
      .open(DATA_CACHE)
      .then((c) =>
        c.put(
          "/__data/" + route,
          new Response(text, { headers: { "Content-Type": "application/json", "X-Saved-At": String(now), "X-Version": version || "" } })
        )
      )
      .catch(() => {});
  } catch (e) {
    /* pamięć to dodatek — nigdy nie przeszkadza w działaniu appki */
  }
}

// strict=true: nieudany odczyt RZUCA błąd zamiast zwracać null. Używane tam,
// gdzie "brak danych" oznaczałby pustą listę i groził jej zapisaniem/
// wyświetleniem zamiast prawdziwych danych (dopisywanie do czatu, odświeżanie).
async function readRoute(key, route, strict, patient) {
  const result = await fetchRouteData(route, patient ? PATIENT_ATTEMPTS : null);
  if (!result.ok) {
    if (strict) throw new Error(`Nie udało się pobrać danych (${route}) — sprawdź połączenie z internetem.`);
    return null;
  }
  persistRoute(route, result.text, result.version, false);
  return { key, value: JSON.stringify(result.data), shared: true, version: result.version };
}

// Start appki czytał ~24 kolekcji JEDNA PO DRUGIEJ (App.jsx robi `await` na
// każdej z nich po kolei) — przy ~200-300 ms na zapytanie to kilka sekund
// przy dobrym zasięgu, a przy słabym dużo dłużej. Dlatego już TERAZ, zanim
// appka w ogóle się uruchomi, rozpoczynamy pobieranie wszystkich tych
// samych kolekcji równolegle (max 6 naraz — tyle połączeń przeglądarka
// dopuszcza do jednego serwera). Kiedy App.jsx zapyta później o daną
// kolekcję po raz pierwszy, dostaje wynik, który już jest w drodze albo
// gotowy. Każdy wynik można odebrać tylko RAZ i tylko przez ograniczony czas
// (PREFETCH_MAX_AGE_MS) — kolejne odczyty (odświeżanie w tle) zawsze idą do
// serwera po świeże dane, tak jak wcześniej.
// Dane potrzebne kierowcy (najpierw małe i najważniejsze, największe — czat — na końcu).
const DRIVER_PREFETCH_ROUTES = [
  "drivers",
  "earnings",
  "bonuses",
  "avatars",
  "payout-decisions",
  "compensation-requests",
  "news",
  "tasks",
  "point-adjustments",
  "debt-repayments",
  "driver-debts",
  // czat (wątki + grupa) celowo POZA pobieraniem startowym — najwięcej danych; appka dociąga go po pokazaniu ekranu
];
// Dane finansowe firmy, krypto itp. — potrzebne WYŁĄCZNIE panelowi admina. Kierowca
// nie musi ich pobierać przy każdym starcie (mniej zapytań, szybszy start,
// i telefony kierowców nie ściągają danych firmy). Panel admina doczytuje je sam.
const ADMIN_ONLY_PREFETCH_ROUTES = [
  "cars",
  "weekly-revenue",
  "profit-loss",
  "car-repairs",
  "company-expenses",
  "car-info",
  "company-adjustments",
  "crypto-paper-portfolio",
  "crypto-strategy-params",
  "crypto-intraday-portfolio",
  "crypto-intraday-strategy-params",
];
let IS_ADMIN_START = false;
try {
  IS_ADMIN_START = localStorage.getItem("patex_admin_session") === "true";
} catch (e) {
  IS_ADMIN_START = false;
}
const PREFETCH_ROUTES = IS_ADMIN_START ? [...DRIVER_PREFETCH_ROUTES, ...ADMIN_ONLY_PREFETCH_ROUTES] : DRIVER_PREFETCH_ROUTES;
const CACHEABLE_AT_START = new Set(DRIVER_PREFETCH_ROUTES); // dane admina zawsze prosto z sieci
window.__patexStartup = { usedCache: false };
async function loadForStartup(route) {
  const key = routeToKey[route];
  if (CACHEABLE_AT_START.has(route)) {
    const cached = await readCachedRoute(route);
    if (cached) {
      window.__patexStartup.usedCache = true;
      return { key, value: cached.text, shared: true, version: cached.version, fromCache: true };
    }
  }
  return readRoute(key, route, false);
}
const PREFETCH_CONCURRENCY = 6;
const PREFETCH_MAX_AGE_MS = 120000;

const prefetched = new Map(); // route -> { promise, startedAt }
const routeToKey = {};
for (const [key, route] of Object.entries(KEY_TO_ROUTE)) routeToKey[route] = key;

function startPrefetch() {
  try {
    const queue = [];
    for (const route of PREFETCH_ROUTES) {
      let resolveFn;
      const promise = new Promise((resolve) => {
        resolveFn = resolve;
      });
      prefetched.set(route, { promise, startedAt: Date.now() });
      queue.push({ route, resolveFn });
    }
    let next = 0;
    async function worker() {
      while (next < queue.length) {
        const { route, resolveFn } = queue[next++];
        try {
          resolveFn(await loadForStartup(route));
        } catch (e) {
          resolveFn(null);
        }
      }
    }
    for (let i = 0; i < PREFETCH_CONCURRENCY; i++) worker();
  } catch (e) {
    prefetched.clear(); // w razie czegokolwiek nieoczekiwanego — wróć do zwykłego zachowania
  }
}
startPrefetch();

// Zapis z wykrywaniem konfliktów (opts.ifVersion = wersja kolekcji, na której
// oparto zmianę) — serwer odrzuca go (412), jeśli ktoś inny zdążył w
// międzyczasie zapisać tę kolekcję, zamiast po cichu nadpisać jego zmiany.
// Wtedy rzucamy błąd z conflict=true, a wywołujący pobiera dane od nowa i
// dokłada swoją zmianę jeszcze raz. Zapisy BEZ opts działają jak dawniej.
const CONFLICT_PUT_TIMEOUT_MS = 45000; // żeby zawieszony zapis nie blokował kolejki wiadomości na zawsze

window.storage = {
  async get(key, shared, opts) {
    const strict = !!(opts && opts.strict);
    const patient = !!(opts && opts.patient);
    const route = KEY_TO_ROUTE[key];
    if (!route) return null; // e.g. the old seed-marker key — no longer needed
    // Odczyt "ścisły" (baza dla zmiany/zapisu) ZAWSZE idzie po świeże dane — nigdy z prefetchu.
    const early = strict ? null : prefetched.get(route);
    if (early) {
      prefetched.delete(route); // jednorazowe — następny odczyt tej kolekcji idzie już do serwera
      if (Date.now() - early.startedAt <= PREFETCH_MAX_AGE_MS) return early.promise;
    }
    return readRoute(key, route, strict, patient);
  },
  async set(key, value, shared, opts) {
    const route = KEY_TO_ROUTE[key];
    if (!route) return null;
    const ifVersion = opts && opts.ifVersion;
    const controller = ifVersion ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), CONFLICT_PUT_TIMEOUT_MS) : null;
    try {
      const res = await fetch(`/api/${route}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(ifVersion ? { "X-If-Version": ifVersion } : {}) },
        body: value,
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (!res.ok) {
        if (res.status === 412 && ifVersion) {
          const conflict = new Error(`Kolekcja "${route}" zmieniła się w międzyczasie.`);
          conflict.conflict = true;
          throw conflict;
        }
        try {
          const errBody = await res.json();
          console.error(`[storage] zapis "${route}" odrzucony (${res.status}):`, errBody.error || errBody);
        } catch (e) {
          console.error(`[storage] zapis "${route}" odrzucony (${res.status})`);
        }
        return null;
      }
      persistRoute(route, value, res.headers.get("X-Collection-Version") || null, true);
      return { key, value, shared: true, version: res.headers.get("X-Collection-Version") || null };
    } catch (e) {
      if (e && e.conflict) throw e;
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  },
  async delete(key) {
    return null; // not used by this app
  },
  async list() {
    return null; // not used by this app
  },
};
